const GERMAN_TIMEZONE = 'Europe/Berlin';

/**
 * Konvertiert UTC-Datum zu datetime-local Format (deutsche Zeit)
 * @param utcDate - UTC Date aus der Datenbank
 * @returns String im Format "YYYY-MM-DDTHH:mm" in deutscher Zeit
 */
export const toDateTimeLocal = (utcDate: Date | string): string => {
  const date = typeof utcDate === 'string' ? new Date(utcDate) : utcDate;
  
  // Konvertiere UTC zu deutscher Zeit
  const germanTime = new Date(date.toLocaleString('en-US', { timeZone: GERMAN_TIMEZONE }));
  
  const year = germanTime.getFullYear();
  const month = String(germanTime.getMonth() + 1).padStart(2, '0');
  const day = String(germanTime.getDate()).padStart(2, '0');
  const hours = String(germanTime.getHours()).padStart(2, '0');
  const minutes = String(germanTime.getMinutes()).padStart(2, '0');
  
  return `${year}-${month}-${day}T${hours}:${minutes}`;
};

/**
 * Konvertiert datetime-local Input (deutsche Zeit) zu UTC ISO String
 * @param dateTimeLocal - String im Format "YYYY-MM-DDTHH:mm" in deutscher Zeit
 * @returns UTC ISO String für API
 */
export const fromDateTimeLocal = (dateTimeLocal: string): string => {
  if (!dateTimeLocal) return '';
  
  console.log('🔄 Converting German datetime-local to UTC:', dateTimeLocal);
  
  // Parse den datetime-local String
  const [datePart, timePart] = dateTimeLocal.split('T');
  const [year, month, day] = datePart.split('-').map(Number);
  const [hours, minutes] = timePart.split(':').map(Number);
  
  console.log('Parsed components:', { year, month, day, hours, minutes });
  
  // Erstelle Date mit expliziter deutscher Zeitzone
  // Verwende die constructor mit Jahr, Monat-1, Tag, Stunde, Minute
  const germanDate = new Date(year, month - 1, day, hours, minutes);
  
  console.log('🇩🇪 German date object:', germanDate);
  console.log('🇩🇪 German date ISO (local):', germanDate.toISOString());
  
  // Das Date-Objekt ist bereits in lokaler Zeit erstellt
  // Um UTC zu bekommen, müssen wir den Browser-Timezone-Offset korrigieren
  // und dann den deutschen Offset anwenden
  
  const browserOffset = germanDate.getTimezoneOffset(); // in Minuten
  console.log('🌍 Browser timezone offset (minutes):', browserOffset);
  
  // Korrigiere zum UTC und dann für deutsche Zeit
  // Deutsche Zeit: UTC+1 (Winter) oder UTC+2 (Sommer)
  const utcDate = new Date(germanDate.getTime() - (browserOffset * 60 * 1000));
  
  // Jetzt haben wir UTC, aber wir müssen noch den deutschen Offset abziehen
  // da wir das als deutsche Zeit interpretiert haben wollen
  const january = new Date(year, 0, 1);
  const july = new Date(year, 6, 1);
  const isDST = germanDate.getTimezoneOffset() < Math.max(january.getTimezoneOffset(), july.getTimezoneOffset());
  
  const germanOffsetHours = isDST ? 2 : 1; // Sommer: UTC+2, Winter: UTC+1
  const finalUTCDate = new Date(utcDate.getTime() - (germanOffsetHours * 60 * 60 * 1000));
  
  console.log('🌞 Is DST:', isDST);
  console.log('🇩🇪 German offset hours:', germanOffsetHours);
  console.log('✅ Final UTC date:', finalUTCDate);
  console.log('✅ Final UTC ISO:', finalUTCDate.toISOString());
  
  return finalUTCDate.toISOString();
};

/**
 * Formatiert UTC-Datum für deutsche Anzeige
 * @param utcDate - UTC Date aus der Datenbank
 * @param options - Formatierungsoptionen
 * @returns Formatierter String in deutscher Zeit
 */
export const formatGermanDateTime = (
  utcDate: Date | string, 
  options: Intl.DateTimeFormatOptions = {}
): string => {
  const date = typeof utcDate === 'string' ? new Date(utcDate) : utcDate;
  
  const defaultOptions: Intl.DateTimeFormatOptions = {
    timeZone: GERMAN_TIMEZONE,
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    ...options
  };
  
  return date.toLocaleString('de-DE', defaultOptions);
};

/**
 * Formatiert nur die Zeit in deutscher Zeitzone
 * @param utcDate - UTC Date aus der Datenbank
 * @returns Zeit-String im Format "HH:mm" in deutscher Zeit
 */
export const formatGermanTime = (utcDate: Date | string): string => {
  const date = typeof utcDate === 'string' ? new Date(utcDate) : utcDate;
  
  return date.toLocaleTimeString('de-DE', {
    timeZone: GERMAN_TIMEZONE,
    hour: '2-digit',
    minute: '2-digit'
  });
};

/**
 * Heutiges Datum in deutscher Zeit für Filter
 * @returns Datum-String im Format "YYYY-MM-DD"
 */
export const getTodayDateString = (): string => {
  return new Date().toLocaleDateString('sv-SE', { timeZone: GERMAN_TIMEZONE });
};

/**
 * Erstellt Start- und Endzeit für einen Tag (deutsche Zeit → UTC)
 * @param dateString - Datum im Format "YYYY-MM-DD"
 * @returns Objekt mit start und end UTC ISO Strings
 */
export const getDayBounds = (dateString: string) => {
  const start = fromDateTimeLocal(dateString + 'T00:00');
  const end = fromDateTimeLocal(dateString + 'T23:59');
  
  return { start, end };
};